import Navbar from 'components/navbar';
import { HsRoomPriorityItem } from 'Interfaces/HsRoomPriorityInterfaces';
import { PrinterIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useMemo, useRef } from 'react';
import { commaSeparatedNumbers } from '../../utils/commaSeparatedNumbers';
import RoomHeader from '../CalculationsScreen/utils/RoomHeader';
import classes from './hsRoomPriorities.module.scss';
import useHsRoomPriority from './useHsRoomPriority';

// Add a style element to ensure the page takes full height and hide navbar in print
const fullHeightStyle = (
  <style>
    {`
        html, body, #root {
          height: 100%;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
        
        @media print {
          @page {
            margin: 0.5cm;
            size: landscape;
          }
          
          nav, .navbar { 
            display: none !important;
            height: 0 !important;
            visibility: hidden !important;
          }
          
          /* Make all text bold and increase font size in print */
          * {
            font-weight: bold !important;
          }
          
          /* Ensure consistent font sizes for all print elements */
          .printHeader div {
            font-weight: bold !important;
          }
          
          /* Make sure NDC and Job columns don't wrap */
          .p-datatable-tbody > tr > td:nth-child(2),
          .p-datatable-thead > tr > th:nth-child(2),
          .p-datatable-tbody > tr > td:nth-child(5),
          .p-datatable-thead > tr > th:nth-child(5) {
            white-space: nowrap !important;
            word-wrap: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }
          
          /* Increase all font sizes by 1.5x for print */
          .p-datatable-tbody > tr > td,
          .p-datatable-thead > tr > th {
            font-size: 21px !important;
            font-weight: bold !important;
            text-align: center !important;
            vertical-align: middle !important;
          }
          
          .printHeader div:first-child {
            font-size: 30px !important;
          }
          
          .printHeader div:nth-child(2),
          .printHeader div:nth-child(3) {
            font-size: 24px !important;
          }
          
          .printHeaderRepeated div:first-child {
            font-size: 28px !important;
          }
          
          .printHeaderRepeated div:nth-child(2),
          .printHeaderRepeated div:nth-child(3) {
            font-size: 21px !important;
          }
        }
        
        /* Global styles to force center alignment on ALL table headers */
        .p-datatable .p-datatable-thead > tr > th,
        .p-datatable-thead > tr > th,
        .p-datatable .p-datatable-tbody > tr > td,
        .p-datatable-tbody > tr > td {
          text-align: center !important;
          vertical-align: middle !important;
        }
        
        /* Even more specific selectors */
        .p-datatable table thead tr th,
        .p-datatable table tbody tr td {
          text-align: center !important;
          vertical-align: middle !important;
        }
        
        /* Force center alignment with highest specificity */
        table th, table td {
          text-align: center !important;
          vertical-align: middle !important;
        }
        
        /* SPECIFIC TARGETING FOR JOB AND DESCRIPTION COLUMNS */
        /* Job column (2nd column) - Force center alignment */
        .p-datatable-thead > tr > th:nth-child(2),
        .p-datatable-tbody > tr > td:nth-child(2),
        .p-datatable table thead tr th:nth-child(2),
        .p-datatable table tbody tr td:nth-child(2) {
          text-align: center !important;
          vertical-align: middle !important;
          text-align-last: center !important;
        }
        
        /* Description column (6th column) - Force center alignment */
        .p-datatable-thead > tr > th:nth-child(6),
        .p-datatable-tbody > tr > td:nth-child(6),
        .p-datatable table thead tr th:nth-child(6),
        .p-datatable table tbody tr td:nth-child(6) {
          text-align: center !important;
          vertical-align: middle !important;
          text-align-last: center !important;
        }
        
        /* Force center alignment on ALL nested elements in Job and Description columns */
        .p-datatable-thead > tr > th:nth-child(2) *,
        .p-datatable-tbody > tr > td:nth-child(2) *,
        .p-datatable-thead > tr > th:nth-child(6) *,
        .p-datatable-tbody > tr > td:nth-child(6) * {
          text-align: center !important;
          text-align-last: center !important;
        }

        /* ULTRA-SPECIFIC TARGETING FOR JOB, NDC, AND DESCRIPTION COLUMNS */
        /* Job column (2nd column) - Maximum specificity */
        .hsRoomPrioritiesContainer .p-datatable-thead > tr > th:nth-child(2),
        .hsRoomPrioritiesContainer .p-datatable-tbody > tr > td:nth-child(2),
        .hsRoomPrioritiesContainer .dataTable .p-datatable-thead > tr > th:nth-child(2),
        .hsRoomPrioritiesContainer .dataTable .p-datatable-tbody > tr > td:nth-child(2),
        .hsRoomPrioritiesContainer .dataTable .p-datatable .p-datatable-thead > tr > th:nth-child(2),
        .hsRoomPrioritiesContainer .dataTable .p-datatable .p-datatable-tbody > tr > td:nth-child(2) {
          text-align: center !important;
          vertical-align: middle !important;
          text-align-last: center !important;
          direction: ltr !important;
          writing-mode: horizontal-tb !important;
          justify-content: center !important;
          align-items: center !important;
        }

        /* NDC column (5th column) - Maximum specificity */
        .hsRoomPrioritiesContainer .p-datatable-thead > tr > th:nth-child(5),
        .hsRoomPrioritiesContainer .p-datatable-tbody > tr > td:nth-child(5),
        .hsRoomPrioritiesContainer .dataTable .p-datatable-thead > tr > th:nth-child(5),
        .hsRoomPrioritiesContainer .dataTable .p-datatable-tbody > tr > td:nth-child(5),
        .hsRoomPrioritiesContainer .dataTable .p-datatable .p-datatable-thead > tr > th:nth-child(5),
        .hsRoomPrioritiesContainer .dataTable .p-datatable .p-datatable-tbody > tr > td:nth-child(5) {
          text-align: center !important;
          vertical-align: middle !important;
          text-align-last: center !important;
          direction: ltr !important;
          writing-mode: horizontal-tb !important;
          justify-content: center !important;
          align-items: center !important;
        }

        /* Description column (6th column) - Maximum specificity */
        .hsRoomPrioritiesContainer .p-datatable-thead > tr > th:nth-child(6),
        .hsRoomPrioritiesContainer .p-datatable-tbody > tr > td:nth-child(6),
        .hsRoomPrioritiesContainer .dataTable .p-datatable-thead > tr > th:nth-child(6),
        .hsRoomPrioritiesContainer .dataTable .p-datatable-tbody > tr > td:nth-child(6),
        .hsRoomPrioritiesContainer .dataTable .p-datatable .p-datatable-thead > tr > th:nth-child(6),
        .hsRoomPrioritiesContainer .dataTable .p-datatable .p-datatable-tbody > tr > td:nth-child(6) {
          text-align: center !important;
          vertical-align: middle !important;
          text-align-last: center !important;
          direction: ltr !important;
          writing-mode: horizontal-tb !important;
          justify-content: center !important;
          align-items: center !important;
        }

        /* Force all nested elements in these columns to center */
        .hsRoomPrioritiesContainer .p-datatable-thead > tr > th:nth-child(2) *,
        .hsRoomPrioritiesContainer .p-datatable-tbody > tr > td:nth-child(2) *,
        .hsRoomPrioritiesContainer .p-datatable-thead > tr > th:nth-child(5) *,
        .hsRoomPrioritiesContainer .p-datatable-tbody > tr > td:nth-child(5) *,
        .hsRoomPrioritiesContainer .p-datatable-thead > tr > th:nth-child(6) *,
        .hsRoomPrioritiesContainer .p-datatable-tbody > tr > td:nth-child(6) * {
          text-align: center !important;
          text-align-last: center !important;
          direction: ltr !important;
          writing-mode: horizontal-tb !important;
          justify-content: center !important;
          align-items: center !important;
        }
      `}
  </style>
);

// Create a custom RoomHeader component that includes the date/shift filter info
const RoomHeaderWithFilter = ({
  name,
  isExpanded,
  onToggle,
  currentFilter,
  isPrinting
}: {
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
  currentFilter?: { date: string; shift: string };
  isPrinting?: boolean;
}) => {
  return (
    <div className={classes.customRoomHeader}>
      <div className={classes.roomHeaderMain}>
        <RoomHeader name={name} isExpanded={isExpanded} onToggle={onToggle} isPrinting={isPrinting} />
        {currentFilter && !isPrinting && (
          <div className={classes.filterInfoHeader}>
            Viewing: {currentFilter.date} | {currentFilter.shift} Shift
          </div>
        )}
      </div>
    </div>
  );
};

const HsRoomPriorities: React.FC = observer(() => {
  const {
    hsRoomPriorityStore,
    expandedRooms,
    hasReceivedResponse,
    loading,
    loadingShifts,
    toggleRoom,
    hasValidData,
    getRoomNameFromCode,
    formatDateForDisplay,
    shifts,
    setExpandedRooms,
    isPrinting,
    prepareForPrinting,
    completePrinting,
    getRoomFilter,
    globalDateFilter,
    globalShiftFilter,
    handleGlobalDateChange,
    handleGlobalShiftChange,
    applyGlobalFilters
  } = useHsRoomPriority();

  // Reference to the printable content
  const printRef = useRef<HTMLDivElement>(null);

  // Create an empty item template - use undefined for all fields to ensure they render as empty
  const emptyItem = useMemo(
    () => ({
      // complete: undefined, // Removed
      tentativeCleaningLevel: undefined,
      job: undefined,
      job_type: undefined, // Added
      priority: undefined,
      ndc: undefined,
      description: undefined,
      qty_containers_to_fill: undefined,
      qty_per_container: undefined,
      bottle_stock_code: undefined,
      bottle_description: undefined, // Added
      bottles_left_to_fill: undefined,
      qty_containers_filled_in_shift: undefined
    }),
    []
  );

  // Custom formatters that return empty string for undefined values
  const formatValueOrEmpty = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  };

  // Numeric formatters that use comma separation for defined values
  const formatNumericValueOrEmpty = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null) {
      return '';
    }
    return commaSeparatedNumbers(value);
  };

  // Direct browser print method (simpler approach)
  const handleDirectPrint = async () => {
    try {
      await prepareForPrinting();

      // Expand all rooms for printing
      const allRoomCodes = hsRoomPriorityStore.roomCodes;
      const expandedState = { ...expandedRooms };

      // Set all rooms to expanded state for printing
      allRoomCodes.forEach(code => {
        expandedState[code] = true;
      });

      setExpandedRooms(expandedState);

      // Wait a moment for the UI to update
      setTimeout(() => {
        window.print();
        completePrinting();
      }, 100);
    } catch (error) {
      completePrinting();
    }
  };

  // Show loading spinner when data is being fetched
  if ((loading && !hasReceivedResponse) || loadingShifts) {
    return (
      <div className={classes.hsRoomPrioritiesContainer}>
        {fullHeightStyle}
        <div className={classes.navbarContainer}>
          <Navbar heading="Room Schedule" />
        </div>
        <div className={classes.content}>
          <div className={classes.loadingContainer}>
            <ProgressSpinner className={classes.spinner} />
            <div>Loading Room Schedule...</div>
          </div>
        </div>
      </div>
    );
  }

  // Prepare shift options for dropdown
  const shiftOptions = shifts.map(shift => ({
    label: shift.shift_name,
    value: shift.shift_name
  }));

  // Render the comments section with shifts
  const renderCommentsSection = () => {
    return (
      <div className={classes.commentsSection}>
        <div className={classes.commentsSectionHeader}>COMMENTS</div>
        <div className={classes.shiftsContainer}>
          {shifts.map(shift => (
            <div key={shift.id} className={classes.shiftRow}>
              <div className={classes.shiftName}>{shift.shift_name} Shift:</div>
              <div className={classes.shiftCommentSpace}></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Get the current date for print header
  const printDateTime = new Date().toLocaleString();

  // Function to ensure a minimum of 5 rows
  const ensureMinimumRows = (items: HsRoomPriorityItem[]): HsRoomPriorityItem[] => {
    if (items.length === 0) {
      // If no items, return 5 empty rows
      return Array(5).fill(emptyItem);
    } else if (items.length < 5) {
      // If fewer than 5 items, add empty rows to reach 5
      const emptyRowsNeeded = 5 - items.length;
      return [...items, ...Array(emptyRowsNeeded).fill(emptyItem)];
    }
    // If 5 or more items, return as is
    return items;
  };

  return (
    <div className={`${classes.hsRoomPrioritiesContainer} ${isPrinting ? classes.isPrinting : ''}`}>
      {fullHeightStyle}

      <div className={classes.navbarContainer}>
        <Navbar heading="Room Schedule" />
      </div>

      <div className={classes.content}>
        <div className={classes.headerRow}>
          {/* Left side - global filters */}
          <div className={classes.globalFilters}>
            <div className={classes.filterItem}>
              <span className={classes.filterLabel}>Date:</span>
              <Calendar
                value={globalDateFilter}
                onChange={e => handleGlobalDateChange(e.value as Date)}
                dateFormat="mm/dd/yy"
                showIcon
                className={classes.datePicker}
              />
            </div>
            <div className={classes.filterItem}>
              <span className={classes.filterLabel}>Shift:</span>
              <Dropdown
                value={globalShiftFilter}
                options={shiftOptions}
                onChange={e => handleGlobalShiftChange(e.value)}
                className={classes.shiftDropdown}
                disabled={loadingShifts}
                placeholder={loadingShifts ? 'Loading...' : 'Select Shift'}
              />
            </div>
            <Button
              label="Apply Filters"
              className={classes.applyFiltersButton}
              onClick={applyGlobalFilters}
              disabled={loading}
              loading={loading}
            />
          </div>

          {/* Right side - print button */}
          <div className={classes.printButtonContainer}>
            <Button
              id="print-btn"
              icon={<PrinterIcon color="#4D2E6D" />}
              rounded
              style={{ backgroundColor: '#D6B1FD', border: 'none' }}
              onClick={handleDirectPrint}
            />
          </div>
        </div>

        {/* Always show the filter controls even if no data */}
        {/* Only show empty message when we have a response but no data and not rooms */}
        {hasReceivedResponse && !hasValidData() && hsRoomPriorityStore.roomCodes.length === 0 ? (
          <div className={classes.emptyMessage}>
            No room priority data found for the selected date and shift. Please try different
            filters.
          </div>
        ) : (
          /* Printable content */
          <div ref={printRef} className={classes.printableContent}>
            {/* Print header - only visible when printing - shows at top of document */}
            <div className={classes.printHeader}>
              <div>Room Schedule</div>
              <div>
                {formatDateForDisplay(globalDateFilter)} | Shift: {globalShiftFilter}
              </div>
              <div>Printed: {printDateTime}</div>
            </div>

            {hsRoomPriorityStore.roomCodes.map((roomCode, index) => {
              const roomItems = hsRoomPriorityStore.getRoomItems(roomCode);
              const roomName = getRoomNameFromCode(roomCode);
              const currentRoomFilter = getRoomFilter(roomCode);

              // Format the date for display in the filter info
              const formattedDate = currentRoomFilter
                ? formatDateForDisplay(currentRoomFilter.date)
                : '';

              // Always ensure minimum 5 rows for every room
              const displayItems = ensureMinimumRows(roomItems);

              return (
                <div
                  key={roomCode}
                  className={`${classes.fullRoomSection} ${index > 0 ? classes.pageBreakBefore : ''}`}
                >
                  {/* Add print header for each room section after the first one */}
                  {isPrinting && index > 0 && (
                    <div className={classes.printHeaderRepeated}>
                      <div>Room Schedule</div>
                      <div>
                        {formatDateForDisplay(globalDateFilter)} | Shift: {globalShiftFilter}
                      </div>
                      <div>Printed: {printDateTime}</div>
                    </div>
                  )}

                  <div className={classes.roomSection}>
                    <div className={classes.roomHeaderContainer}>
                      <RoomHeaderWithFilter
                        name={roomName}
                        isExpanded={!!expandedRooms[roomCode] || isPrinting}
                        onToggle={() => toggleRoom(roomCode)}
                        currentFilter={
                          currentRoomFilter
                            ? {
                              date: formattedDate,
                              shift: currentRoomFilter.shift
                            }
                            : undefined
                        }
                        isPrinting={isPrinting}
                      />
                    </div>

                    {(expandedRooms[roomCode] || isPrinting) && (
                      <DataTable
                        value={displayItems}
                        className={classes.dataTable}
                        rowClassName={data =>
                          data.job === undefined ? classes.emptyRow : classes.tableRow
                        }
                        resizableColumns={false}
                        columnResizeMode="fit"
                        scrollable={false}
                        stripedRows
                        style={{
                          textAlign: 'center'
                        }}
                        tableStyle={{
                          textAlign: 'center'
                        }}
                      >
                        <Column
                          field="tentativeCleaningLevel"
                          header="Tentative Cleaning Level"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatValueOrEmpty(rowData.tentativeCleaningLevel)
                          }
                          style={{ width: '100px', maxWidth: '100px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                        <Column
                          field="job"
                          header="Job"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) => formatValueOrEmpty(rowData.job)}
                          style={{
                            width: '60px',
                            maxWidth: '60px',
                            textAlign: 'center',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                        />
                        <Column
                          field="job_type"
                          header="Job Type"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatValueOrEmpty(rowData.job_type)
                          }
                          style={{ width: '80px', maxWidth: '80px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                        <Column
                          field="priority"
                          header="Prty"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatValueOrEmpty(rowData.priority)
                          }
                          style={{ width: '55px', maxWidth: '55px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                        <Column
                          field="ndc"
                          header="NDC"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) => formatValueOrEmpty(rowData.ndc)}
                          style={{
                            width: '100px',
                            maxWidth: '100px',
                            textAlign: 'center',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                        />
                        <Column
                          field="description"
                          header="Description"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatValueOrEmpty(rowData.description)
                          }
                          style={{
                            width: '180px',
                            maxWidth: '180px',
                            textAlign: 'center',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center',
                            direction: 'ltr',
                            writingMode: 'horizontal-tb'
                          }}
                        />
                        <Column
                          field="qty_containers_to_fill"
                          header="Qty of Containers to Fill"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatNumericValueOrEmpty(rowData.qty_containers_to_fill)
                          }
                          style={{ width: '100px', maxWidth: '100px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                        <Column
                          field="qty_per_container"
                          header="Qty per container"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatNumericValueOrEmpty(rowData.qty_per_container)
                          }
                          style={{ width: '80px', maxWidth: '80px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                        <Column
                          field="bottle_description"
                          header="Bottle Description"
                          className={classes.column}
                          body={(rowData: HsRoomPriorityItem) =>
                            formatValueOrEmpty(rowData.bottle_description)
                          }
                          style={{ width: '115px', maxWidth: '115px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                        <Column
                          field="qty_containers_filled_in_shift"
                          header="Qty of Containers Filled in Shift"
                          className={classes.column}
                          body={() => ''}
                          style={{ width: '80px', maxWidth: '80px', textAlign: 'center' }}
                          headerStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                          bodyStyle={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            textAlignLast: 'center'
                          }}
                        />
                      </DataTable>
                    )}
                  </div>

                  {/* Comments section after each room - only show when not printing */}
                  {(expandedRooms[roomCode] || isPrinting) && !isPrinting && renderCommentsSection()}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default HsRoomPriorities;
